import { useMemo, useState } from 'react'
import {
  attributionSurveyManager,
  channelsToTranslatedChannels,
} from '../../../services/AttributionSurveyManager'
import * as S from './AttributionChannelSelector.styles'
import AttributionChannelSelectorExpanded from './AttributionChannelSelectorExpanded'
import useScreenSize from '../../../hooks/useScreenSize'
import {
  EXPANDED_CHANNELS,
  getSubAttributionOptions,
} from './subattributions.consts'
import { useTranslation } from 'react-i18next'
import { useKeys } from 'services/KeysProvider'
import {
  AUSTRALIA,
  IRELAND,
  UNITED_KINGDOM,
  UNITED_STATES,
} from 'config/countries-data'
import { analytics, EVENT_NAMES } from '../../../services/Analytics/Analytics'

type AttributionChannel = keyof typeof channelsToTranslatedChannels

const ATTRIBUTION_CHANNEL_OVERRIDES_BY_COUNTRY: Record<
  string,
  Partial<Record<AttributionChannel, AttributionChannel>>
> = {
  [UNITED_KINGDOM]: { Airport: 'StaceySolomon' },
  [IRELAND]: { Airport: 'StaceySolomon' },
  [AUSTRALIA]: { Airport: 'RebeccaJudd', Game: 'TamminSursok' },
  [UNITED_STATES]: { Airport: 'TiaMowry' },
}

function getAttributionChannels(channels: string[], country: string): string[] {
  const channelOverrides: Record<string, AttributionChannel | undefined> =
    ATTRIBUTION_CHANNEL_OVERRIDES_BY_COUNTRY[country]
  if (!channelOverrides) {
    return channels
  }
  return channels.map((channel) => channelOverrides[channel] ?? channel)
}

type Props = {
  onClick: ({
    e,
    channelName,
    subChannel,
  }: {
    e: React.MouseEvent<HTMLDivElement>
    channelName: string
    subChannel?: string
  }) => void
  source: string
}

function AttributionChannelSelector({ onClick, source }: Props) {
  const { t } = useTranslation()
  const [expandedChannelSelected, setExpandedChannelSelected] = useState('')
  const isMediumScreen = useScreenSize('md')
  const { ipCountry } = useKeys()
  const country = ipCountry?.toUpperCase() ?? ''
  const initialChannels = attributionSurveyManager.getAttributionChannelsList()
  const attributionChannels = useMemo(
    () => getAttributionChannels(initialChannels, country),
    [initialChannels, country]
  )

  const getTransformOrigin = (
    index: number,
    attributionsAmount: number
  ): string | undefined => {
    const gridIndex = index + 1
    if (isMediumScreen) {
      switch (gridIndex % 3) {
        case 1:
          return 'top left'
        case 2:
          return 'top center'
        case 0:
          return 'top right'
      }
    }
    if (index >= attributionsAmount - 4) {
      switch (gridIndex % 2) {
        case 1:
          return 'bottom left'
        case 0:
          return 'bottom right'
      }
    }
    switch (gridIndex % 2) {
      case 1:
        return 'top left'
      case 0:
        return 'top right'
    }
  }

  const buttonClicked = (e: React.MouseEvent<HTMLDivElement>, name: string) => {
    if (EXPANDED_CHANNELS.includes(name)) {
      // Fire on the channel tap (before the sub-popup) so we can measure drop-off
      // between opening an expanded channel's popup and picking a sub-option.
      analytics.track(EVENT_NAMES.ATTRIBUTION_SURVEY_EXPANDED_CHANNEL_TAPPED, {
        Answer: name,
        Source: source,
      })
      setExpandedChannelSelected(name)
    } else {
      onClick({ e, channelName: name })
    }
  }

  const attributionChannelItems = attributionChannels.map(
    (attributionChannel, index) => (
      <S.AttributionChannelItem
        key={attributionChannel}
        onClick={(e) => buttonClicked(e, attributionChannel)}
      >
        {t(
          (channelsToTranslatedChannels as Record<string, string>)[
            attributionChannel
          ]
        )}
        {EXPANDED_CHANNELS.includes(attributionChannel) && (
          <AttributionChannelSelectorExpanded
            key={attributionChannel}
            title={attributionChannel}
            isSelected={expandedChannelSelected === attributionChannel}
            transformOrigin={getTransformOrigin(
              index,
              attributionChannels.length
            )}
            options={getSubAttributionOptions(country)}
            onOptionClick={onClick}
            onClickAway={(e) => {
              e.stopPropagation()
              setExpandedChannelSelected('')
            }}
          />
        )}
      </S.AttributionChannelItem>
    )
  )
  return (
    <S.AttributionChannelSelector>
      {attributionChannelItems}
    </S.AttributionChannelSelector>
  )
}

export default AttributionChannelSelector
